"use client";
import React, {Fragment} from "react";
import Image, {StaticImageData} from "next/image";
import {Heading, Box, Icon, Circle, Text, Stack, HStack} from "@chakra-ui/react";
import {IconType} from "@onemind-services-llc/react-icons-ng";
import {DefaultHoverEffect} from "styles/hoverPresets";
import {useTextVariants} from "@/theme/components/useSectionHeading";
import {HEADING_VARIANTS} from "@/theme/components/heading";

export type CardProps = {
  content?: React.ReactNode;
  icon?: IconType;
  iconWithCircle?: boolean;
  image?: string | StaticImageData;
  whiteImage?: string | StaticImageData;
  subTitle?: string;
  title: string;
  conclusion?: string;
  hoverEffect?: boolean;
  hover?: Record<string, any>; // optional custom hover styles
};

const Card = (props: CardProps) => {
  const {
    content,
    icon,
    iconWithCircle = true,
    image,
    whiteImage,
    subTitle,
    title,
    conclusion,
    hoverEffect = false,
    hover = DefaultHoverEffect,
  } = props;
  const textVariant = useTextVariants();
  return (
    <Stack
      role={
        hoverEffect ? (hover === DefaultHoverEffect ? "group" : undefined) : undefined
      }
      className={`brand-shadow ${hoverEffect ? (whiteImage ? "whiteImg" : "") : ""}`}
      borderWidth={"1px"}
      borderColor={"primary.300"}
      backgroundColor={"white"}
      borderRadius={"32px"}
      p={6}
      spacing={6}
      transition="transform 0.2s ease, box-shadow 0.3s ease, background 0.000001s ease"
      _hover={hoverEffect ? hover : undefined}
    >
      <HStack spacing={4}>
        {icon &&
          (iconWithCircle ? (
            <Circle
              size="52px"
              border="2px solid"
              borderColor="primary.200"
              color="primary.200"
              _groupHover={
                hover === DefaultHoverEffect ? {color: "white", borderColor: "white"} : {}
              }
            >
              <Icon as={icon} w={8} h={8} />
            </Circle>
          ) : (
            <Icon
              as={icon}
              w={8}
              h={8}
              color="primary.200"
              _groupHover={
                hover === DefaultHoverEffect ? {color: "white", borderColor: "white"} : {}
              }
            />
          ))}
        {(image || whiteImage) && (
          <Box
            position="relative"
            minW={{base: "40px", xl: "50px"}}
            minH={{base: "40px", xl: "50px"}}
          >
            {image && <Image alt={title} src={image} fill />}
            {whiteImage && (
              <Image
                alt={`${title}-hover`}
                src={whiteImage}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  transition: "opacity 0.3s ease",
                }}
                className="hover-image"
              />
            )}
          </Box>
        )}
        <Heading as="h2" variant={HEADING_VARIANTS.SUBTITLE1} fontWeight={600}>
          {title}
        </Heading>
      </HStack>
      {subTitle && (
        <Text
          color="text.100"
          fontWeight={550}
          variant={textVariant}
          _groupHover={hover === DefaultHoverEffect ? {color: "white"} : {}}
        >
          {subTitle}
        </Text>
      )}
      {content && (
        <Fragment>
          {typeof content === "string" ? (
            <Text
              variant={textVariant}
              color={"text.100"}
              _groupHover={
                hover === DefaultHoverEffect ? {color: "white", borderColor: "white"} : {}
              }
            >
              {content}
            </Text>
          ) : (
            <Box color="inherit" height={"100%"}>
              {content}
            </Box>
          )}
        </Fragment>
      )}
      {conclusion && (
        <Text variant={textVariant} color="text.100" fontStyle={"italic"}>
          {conclusion}
        </Text>
      )}
    </Stack>
  );
};

export default Card;
